import { Track } from 'livekit-client';
import * as React from 'react';
import { useRef, useState } from 'react';
import { DisconnectButton, useLocalParticipantPermissions, usePersistentUserChoices, useTrackToggle } from '@livekit/components-react';
import s from './control-bar.module.scss'
import classNames from 'classnames';
import {
	IconDotsVertical,
	IconLayoutGrid,
	IconMaximize,
	IconPictureInPicture,
	IconScreenShare,
	IconScreenShareOff,
	IconSettings,
	IconX,
} from '@tabler/icons-react/dist/cjs/tabler-icons-react';
import { useConferenceSettingsModal } from '@features/appointment/conference-settings';
import { useClickOutside } from '@utils/lib/useClickOutside';
import { isScreenShareSupported } from '../../lib/screen-share';
import { TrackToggle } from '../track-toggle/track-toggle';
import useMedia from 'use-media';

export const LAYOUT_MODES = {
	compact: 'compact',
	fullscreen: 'fullscreen',
};

export function ControlBar({
  controls,
  saveUserChoices = true,
	hideLogout,
	className,
	isFullscreen = false,
	isTeacher = false,
	onSelectLayout,
  ...props
}) {
  const visibleControls = { leave: true, ...controls };

  const localPermissions = useLocalParticipantPermissions();
	const {open: openSettings} = useConferenceSettingsModal()

	const [isLayoutMenuOpen, setLayoutMenuOpen] = useState(false)
	const [isActionsMenuOpen, setActionsMenuOpen] = useState(false)
	const layoutMenuRef = useRef(null)
	const actionsMenuRef = useRef(null)

	useClickOutside(layoutMenuRef, () => setLayoutMenuOpen(false))
	useClickOutside(actionsMenuRef, () => setActionsMenuOpen(false))

	const screenShareToggle = useTrackToggle({ source: Track.Source.ScreenShare });
	const screenShareSupported = isScreenShareSupported()
	// На мобильных нет выбора формата отображения и демонстрации экрана —
	// там остаётся только кнопка настроек (как у ученика)
	const isMobile = useMedia({ maxWidth: '768px' })

  if (!localPermissions) {
    visibleControls.camera = false;
    visibleControls.chat = false;
    visibleControls.microphone = false;
  } else {
    visibleControls.camera ??= localPermissions.canPublish;
    visibleControls.microphone ??= localPermissions.canPublish;
    visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
  }

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
  } = usePersistentUserChoices({ preventSave: !saveUserChoices });

  const microphoneOnChange = React.useCallback(
    (enabled, isUserInitiated) =>
      isUserInitiated ? saveAudioInputEnabled(enabled) : null,
    [saveAudioInputEnabled],
  );

  const cameraOnChange = React.useCallback(
    (enabled, isUserInitiated) =>
      isUserInitiated ? saveVideoInputEnabled(enabled) : null,
    [saveVideoInputEnabled],
  );

	const handleSelectLayout = (mode) => {
		onSelectLayout?.(mode)
		setLayoutMenuOpen(false)
	}

	const handleOpenSettings = () => {
		openSettings()
		setActionsMenuOpen(false)
	}

	const handleToggleScreenShare = () => {
		screenShareToggle.buttonProps.onClick()
		setActionsMenuOpen(false)
	}

  return (
    <div {...props} className={classNames(s.controlBar, isFullscreen ? s.isFullscreen : '', className)}>
			<div className={s.togglesGroup}>
				{visibleControls.microphone && (
					<div className="lk-button-group">
						<TrackToggle
							source={Track.Source.Microphone}
							showIcon
							onChange={microphoneOnChange}
						/>
					</div>
				)}
				{visibleControls.camera && (
					<div className="lk-button-group">
						<TrackToggle source={Track.Source.Camera} showIcon onChange={cameraOnChange}/>
					</div>
				)}
				{visibleControls.leave && (
					<DisconnectButton>
						<IconX className="lk-button-icon"/>
					</DisconnectButton>
				)}
			</div>
			<div className={s.actionsGroup}>
				{isMobile || (
					<div className={s.popover} ref={layoutMenuRef}>
						<button
							type="button"
							className={s.circleBtn}
							aria-label="Формат отображения видео"
							onClick={() => setLayoutMenuOpen((prev) => !prev)}
						>
							<IconLayoutGrid size={24}/>
						</button>
						{isLayoutMenuOpen && (
							<div className={s.menu}>
								<button type="button" className={s.menuItem} onClick={() => handleSelectLayout(LAYOUT_MODES.compact)}>
									<IconPictureInPicture size={20}/>
									<span>Компактный</span>
								</button>
								<button type="button" className={s.menuItem} onClick={() => handleSelectLayout(LAYOUT_MODES.fullscreen)}>
									<IconMaximize size={20}/>
									<span>На весь экран</span>
								</button>
							</div>
						)}
					</div>
				)}
				{/* Меню дополнительных действий (настройки, демонстрация экрана) доступно обеим ролям на десктопе;
					на мобильных остаётся только кнопка настроек — getDisplayMedia там не поддерживается */}
				{!isMobile ? (
					<div className={s.popover} ref={actionsMenuRef}>
						<button
							type="button"
							className={s.circleBtn}
							aria-label="Дополнительные действия"
							onClick={() => setActionsMenuOpen((prev) => !prev)}
						>
							<IconDotsVertical size={24}/>
						</button>
						{isActionsMenuOpen && (
							<div className={s.menu}>
								<button type="button" className={s.menuItem} onClick={handleOpenSettings}>
									<IconSettings size={20}/>
									<span>Настройки конференции</span>
								</button>
								{screenShareSupported ? (
									<button
										type="button"
										className={classNames(s.menuItem, screenShareToggle.enabled && s.active)}
										onClick={handleToggleScreenShare}
									>
										{screenShareToggle.enabled
											? <IconScreenShareOff size={20}/>
											: <IconScreenShare size={20}/>}
										<span>{screenShareToggle.enabled ? 'Остановить демонстрацию' : 'Демонстрация экрана'}</span>
									</button>
								) : (
									<div className={classNames(s.menuItem, s.disabled)}>
										<IconScreenShareOff size={20}/>
										<div className={s.itemText}>
											<span>Демонстрация экрана</span>
											<span className={s.hint}>Недоступна на этом устройстве</span>
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				) : (
					<button
						type="button"
						className={s.circleBtn}
						aria-label="Настройки конференции"
						onClick={openSettings}
					>
						<IconSettings size={24}/>
					</button>
				)}
			</div>
		</div>
  );
}
